import { useEffect, useState } from "react";
import api, { qs } from "@/lib/api";
import { SVC_KINDS, STATUS_CLS } from "@/lib/service";
import { pctTone, fmtPct } from "@/lib/dpc";

const TH = "px-2 py-2 text-left text-[11px] uppercase tracking-wider font-semibold text-muted-foreground whitespace-nowrap";

function DayCell({ day, code }) {
  const has = new Set(day.kinds);
  return (
    <td className="px-0.5 py-1" data-testid={`svc-day-${code}-${day.date.slice(8)}`} title={`${day.date} · ${day.kinds.length}/4 files${day.late ? " · late" : ""}${day.kinds.length ? `: ${day.kinds.join(", ")}` : ""}`}>
      <div className={`mx-auto grid h-5 w-5 grid-cols-2 gap-px rounded-[3px] p-px ${day.late ? "ring-1 ring-amber-500" : ""} ${day.status === "missing" ? "bg-red-100" : "bg-secondary"}`}>
        {SVC_KINDS.map((k) => <span key={k.key} className={`rounded-[1px] ${has.has(k.key) ? STATUS_CLS.complete : "bg-transparent"}`} />)}
      </div>
    </td>
  );
}

export function ServiceCompliance({ month }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/service/compliance?${qs({ month })}`).then(({ data }) => setData(data)).catch(() => setData(null));
  }, [month]);
  if (!data) return null;
  const dates = data.dates || [];
  return (
    <div className="border bg-card rounded-md overflow-auto" data-testid="svc-compliance">
      <div className="px-4 py-3 border-b flex flex-wrap items-center gap-3">
        <h3 className="font-heading text-sm font-semibold">Daily upload compliance</h3>
        <span className="text-[11px] text-muted-foreground">All 4 DMS reports per workshop per day · late = first file after {data.cutoff} IST</span>
        <div className="ml-auto flex items-center gap-3 text-[10px] text-muted-foreground">
          <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-emerald-500" /> file received</span>
          <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-red-100 border" /> nothing</span>
          <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm ring-1 ring-amber-500" /> late</span>
        </div>
      </div>
      <table className="text-xs">
        <thead className="border-b">
          <tr>
            <th className={`${TH} sticky left-0 bg-card`}>Workshop</th>
            {dates.map((d) => <th key={d} className="px-0.5 py-1 text-center text-[10px] font-medium text-muted-foreground">{+d.slice(8)}</th>)}
            {["Complete", "Partial", "Missing", "Late", "Streak", "%"].map((h) => <th key={h} className={`${TH} text-right`}>{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {data.rows.map((r) => (
            <tr key={r.code} className="border-b last:border-0 hover:bg-muted/50" data-testid={`svc-compliance-${r.code}`}>
              <td className="px-2 py-1 font-medium whitespace-nowrap sticky left-0 bg-card">{r.code} <span className="text-muted-foreground font-normal">{r.name}</span></td>
              {r.day_status.map((d) => <DayCell key={d.date} day={d} code={r.code} />)}
              <td className="px-2 py-1 num text-right text-emerald-700 font-semibold">{r.complete}</td>
              <td className={`px-2 py-1 num text-right ${r.partial ? "text-amber-700 font-semibold" : ""}`}>{r.partial}</td>
              <td className={`px-2 py-1 num text-right ${r.missing ? "text-red-600 font-semibold" : ""}`}>{r.missing}</td>
              <td className={`px-2 py-1 num text-right ${r.late ? "text-amber-700" : ""}`}>{r.late}</td>
              <td className="px-2 py-1 num text-right">{r.streak}</td>
              <td className={`px-2 py-1 num text-right font-bold ${pctTone(r.pct)}`} data-testid={`svc-compliance-pct-${r.code}`}>{fmtPct(r.pct)}</td>
            </tr>
          ))}
          {data.rows.length === 0 && <tr><td colSpan={dates.length + 7} className="px-3 py-6 text-center text-muted-foreground">No workshops in your scope</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
